import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import InfiniteScroll from "components/elements/InfiniteScroll";
import Typography, { ITypo, ITypoColor } from "components/elements/Typography";
import I18n from "components/materials/I18n";
import NumberUtils from "utils/NumberUtils";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// Marge en bas du tableau avant le bord de la viewport.
// Couvre le padding-bottom de .content du PageTemplate (24px) + la border bottom
// du TableContainer (1px) + un peu de respiration. Sans cette marge, le bottom
// du tableau s'aligne exactement avec la fin du viewport et le padding-bottom
// du parent fait scroller la page de quelques pixels.
const DEFAULT_BOTTOM_MARGIN_PX = 40;

export type IRowProps = { rowKey: string } & Record<string, any>;

type IRow = {
	rowKey?: string;
	content: Record<string, CellContent>;
};

type IProps = {
	columnsHead: readonly TableColumn[];
	rows: IRowProps[];
	onNext?: ((release: () => void, reset?: () => void) => Promise<void> | void) | null;
	heightMode?: TableHeightMode;
	viewportBottomMargin?: number;
};

export type TableHeightMode = "remainingViewport" | "content";

export type TableColumn = {
	headContent: JSX.Element | string;
	key: string;
	formatType?: ECellType;
	widthPercentage?: number;
	canBeNegative?: boolean;
};

type CellContent = {
	cellKey: string;
	value: any;
	cellType?: ECellType | undefined;
	canBeNegative?: boolean;
};

export enum ECellType {
	DEFAULT = "default",
	EURO = "euro",
	EURO_WITHOUT_DECIMALS = "euro_without_decimals",
	UNITS = "units",
	PERCENTAGE = "percentage",
	DATE = "date",
}

export default function MuiTable(props: IProps) {
	const { heightMode = "remainingViewport", viewportBottomMargin = DEFAULT_BOTTOM_MARGIN_PX } = props;
	// Mesure dynamiquement la position du TableContainer dans la viewport pour
	// calculer sa max-height : tout l'espace restant sous lui jusqu'au bord du
	// viewport, moins une petite marge. S'adapte au layout de chaque page sans
	// dépendre d'un calc(100vh - Xpx) figé.
	// Wrapper ref autour de TableContainer. On NE pose PAS ce ref directement
	// sur <TableContainer> car InfiniteScroll fait un React.cloneElement qui
	// écrase le ref. À la place, on enroule TableContainer dans un <div> dont
	// on contrôle le ref, et on cherche le TableContainer en descendant.
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const [maxHeight, setMaxHeight] = useState<string>("80vh");
	const compute = useCallback(() => {
		if (heightMode === "content") return;
		const wrapper = wrapperRef.current;
		if (!wrapper) return;
		// On cherche le TableContainer en descendant car InfiniteScroll fait un
		// React.cloneElement qui écraserait un ref posé directement sur lui.
		const tc = wrapper.querySelector<HTMLElement>(".MuiTableContainer-root") ?? wrapper;
		// Position absolue dans le document (insensible au scroll courant).
		const absoluteTop = tc.getBoundingClientRect().top + window.scrollY;
		// Cas 1 : le tableau est sous le fold à scroll=0 → la page est légitimement
		// scrollable (page détail avec form + onglets sous le pli). On ne borne
		// pas la hauteur du tableau, c'est la page qui scrolle.
		if (absoluteTop >= window.innerHeight - 200) {
			setMaxHeight("none");
			return;
		}
		// Cas 2 : le tableau est au-dessus du fold → on borne pour qu'il scrolle
		// en interne et reste visible dans la viewport.
		const available = window.innerHeight - absoluteTop - viewportBottomMargin;
		setMaxHeight(`${Math.max(available, 200)}px`);
	}, [heightMode, viewportBottomMargin]);
	useLayoutEffect(() => {
		if (heightMode === "content") return;
		const wrapper = wrapperRef.current;
		if (!wrapper) return;
		compute();
		// Plusieurs tentatives échelonnées pour capturer le layout final :
		// MUI ripple, chargement de polices, transition CSS, etc. peuvent décaler
		// le top du tableau après le mount initial.
		const raf = requestAnimationFrame(compute);
		const t1 = setTimeout(compute, 100);
		const t2 = setTimeout(compute, 400);
		window.addEventListener("resize", compute);
		const observer = new ResizeObserver(compute);
		observer.observe(document.body);
		if (wrapper.parentElement) observer.observe(wrapper.parentElement);
		return () => {
			cancelAnimationFrame(raf);
			clearTimeout(t1);
			clearTimeout(t2);
			window.removeEventListener("resize", compute);
			observer.disconnect();
		};
	}, [compute, heightMode]);
	// Recompute quand les rows arrivent : le tableau peut être rendu vide au mount,
	// puis recevoir ses données plus tard et changer la hauteur de son parent.
	useEffect(() => {
		compute();
	}, [compute, props.rows.length]);

	const rows: IRow[] = props.rows.map((rowProps) => {
		const row: IRow = {
			rowKey: rowProps.rowKey,
			content: {},
		};
		props.columnsHead.forEach((column) => {
			const cellContent: CellContent = {
				cellKey: column.key + rowProps.rowKey,
				value: rowProps[column.key],
				cellType: column.formatType,
				canBeNegative: column.canBeNegative,
			};
			row.content[column.key] = cellContent;
		});
		return row;
	});

	return (
		<div ref={wrapperRef} style={{ width: "100%", minWidth: 0 }}>
			<InfiniteScroll orientation="vertical" onNext={props.onNext} offset={0}>
				<TableContainer
					sx={{
						border: "1px solid #DCDCDC",
						// maxHeight calculé dynamiquement selon la position du tableau
						// dans la viewport (cf. useLayoutEffect au-dessus).
						maxHeight: heightMode === "remainingViewport" ? maxHeight : "none",
						overflowY: heightMode === "remainingViewport" ? "auto" : "visible",
						overflowX: "auto",
						// Empêche le rubber band scroll de macOS de remonter au parent
						// et de détacher visuellement le thead sticky.
						overscrollBehavior: heightMode === "remainingViewport" ? "contain" : "auto",
					}}>
					<Table aria-label="simple table" stickyHeader>
						<TableHead sx={{ borderBottom: "1px solid #DCDCDC" }}>
							<TableRow>
								{props.columnsHead.map((column) => (
									<TableCell key={column.key} align={"left"} width={column.widthPercentage?.toString().concat("%")} sx={{ backgroundColor: "#EFEFEF" }}>
										<Typography typo={ITypo.P_SMALL_BOLD}>{column.headContent}</Typography>
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.length === 0 && (
								<TableRow sx={{ backgroundColor: "#FFF", border: "0" }}>
									<TableCell colSpan={props.columnsHead.length} align="center" sx={{ border: "0", padding: "48px 16px" }}>
										<Typography typo={ITypo.P_MEDIUM} color={ITypoColor.WILD_SAND_500}>
											{I18n.asset.common.no_content}
										</Typography>
									</TableCell>
								</TableRow>
							)}
							{rows.map((row, index) => {
								return (
									<TableRow key={row.rowKey} sx={{ backgroundColor: index % 2 ? "#F6F6F6" : "#FFF", border: "0", verticalAlign: "middle" }}>
										{Object.values(row.content).map((cell) => (
											<TableCell key={cell.cellKey} align="left" sx={{ border: "0" }}>
												{toFormat(cell.value, cell.cellType, cell.canBeNegative)}
											</TableCell>
										))}
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</InfiniteScroll>
		</div>
	);

	function toFormat(value: any, formatType: TableColumn["formatType"], canBeNegative: boolean = true) {
		if (value === undefined || value === null) return "-";
		if (!canBeNegative && value < 0) return "-";
		switch (formatType) {
			case ECellType.UNITS:
				return (value ?? 0) + "/units";
			case ECellType.EURO:
				return NumberUtils.formatNumberAddingSpaces(value, 2) + "\xa0€";
			case ECellType.EURO_WITHOUT_DECIMALS:
				return NumberUtils.formatNumberAddingSpaces(value, 0) + "\xa0€";
			case ECellType.PERCENTAGE:
				return value.toFixed(2) + "\xa0%";
			case ECellType.DATE:
				return value ? new Date(value).toLocaleDateString() : "-";
			default:
				return value;
		}
	}
}
